/* eslint-disable no-underscore-dangle */
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { BehaviorSubject } from 'rxjs';
import { TokenKeysEnum } from '@enums';
import { IUser, IEmployeeResponse } from '@models';

type DataType = string | IUser | number | IEmployeeResponse;

@Injectable({
  providedIn: 'root'
})
export class LocalDbService {
  private _accessToken: string = null;
  private _refreshToken: string = null;
  private _accessTokenService: string = null;

  private _user: IUser;
  private _user$: BehaviorSubject<IUser> = new BehaviorSubject<IUser>(null);

  private _employee: IEmployeeResponse;
  private _employee$: BehaviorSubject<IEmployeeResponse> = new BehaviorSubject<IEmployeeResponse>(null);

  private _sessionExpires: number;

  constructor(private storage: Storage) {}

  public get accessToken(): string {
    return this._accessToken;
  }
  public get accessTokenService(): string {
    return this._accessTokenService;
  }

  public get refreshToken(): string {
    return this._refreshToken;
  }

  public get user(): IUser {
    return this._user;
  }

  public get userAsSubject(): BehaviorSubject<IUser> {
    return this._user$;
  }

  public get employee(): IEmployeeResponse {
    return this._employee;
  }

  public get employeeAsSubject(): BehaviorSubject<IEmployeeResponse> {
    return this._employee$;
  }

  public get sessionExpires(): number {
    return this._sessionExpires;
  }

  private set updateAccessToken(data: string) {
    this._accessToken = data;
  }

  private set updateAccessTokenService(data: string) {
    this._accessTokenService = data;
  }

  private set updateRefreshToken(data: string) {
    this._refreshToken = data;
  }

  private set updateUser(data: IUser) {
    this._user = { ...data };
    this._user$.next(this._user);
  }

  private set updateEmployee(data: IEmployeeResponse) {
    this._employee = { ...data };
    this._employee$.next(this._employee);
  }

  private set updateSessionExpires(data: number) {
    this._sessionExpires = data;
  }

  /**
   * @description Permmite guardar el usuario y el token.
   * Util para el login y el registro de usuario
   */
  public async updateBasicInfo(data: {
    user: IUser;
    accessToken: string;
    refreshToken: string;
    timestamp: number;
  }): Promise<void> {
    await this.updateState(TokenKeysEnum.USER, data.user);
    await this.updateState(TokenKeysEnum.ACCESS_TOKEN, data.accessToken);
    await this.updateState(TokenKeysEnum.REFRESH_TOKEN, data.refreshToken);
    await this.updateState(TokenKeysEnum.SESSION_EXPIRES, data.timestamp);
  }

  public async init(): Promise<void> {
    await this.storage.create();
    await this.initFromDB();
  }

  public async initFromDB(): Promise<void> {
    const ACCESS_TOKEN: string = (await this.storage.get(TokenKeysEnum.ACCESS_TOKEN)) || null;
    const REFRESH_TOKEN: string = (await this.storage.get(TokenKeysEnum.REFRESH_TOKEN)) || null;
    const REFRESH_TOKEN_SERVICE: string = (await this.storage.get(TokenKeysEnum.REFRESH_TOKEN_SERVICE)) || null;
    const USER: string = (await this.storage.get(TokenKeysEnum.USER)) || null;
    const EMPLOYEE: string = (await this.storage.get(TokenKeysEnum.EMPLOYEE)) || null;
    const SESSION_EXPIRES: string = (await this.storage.get(TokenKeysEnum.SESSION_EXPIRES)) || null;

    if (!!ACCESS_TOKEN) {
      this.updateAccessToken = JSON.parse(ACCESS_TOKEN) as string;
    } else {
      console.warn('[LOCAL] No hay token de sesion guardado');
    }

    if (!!REFRESH_TOKEN) {
      this.updateRefreshToken = JSON.parse(REFRESH_TOKEN) as string;
    } else {
      console.warn('[LOCAL] No hay token de refresco guardado');
    }

    if (!!REFRESH_TOKEN_SERVICE) {
      this.updateAccessTokenService = JSON.parse(REFRESH_TOKEN_SERVICE) as string;
    } else {
      console.warn('[LOCAL] No hay token de refresco de servicio guardado');
    }

    if (!!USER) {
      this.updateUser = JSON.parse(USER) as IUser;
    } else {
      console.warn('[LOCAL] No hay usuario guardado');
    }

    if (!!EMPLOYEE) {
      this.updateEmployee = JSON.parse(EMPLOYEE) as IEmployeeResponse;
    } else {
      console.warn('[LOCAL] No hay empleado guardado');
    }

    if (!!SESSION_EXPIRES) {
      this.updateSessionExpires = JSON.parse(SESSION_EXPIRES) as number;
    } else {
      console.warn('[LOCAL] No hay fecha de expiracion de la sesion guardada');
    }
  }

  /**
   * @param tokenKey
   * @description Actualizar un key determinado en el local storage
   * @example
   * TokenKeysEnum.USER; // Datos de usuario
   * TokenKeysEnum.TOKEN; // Token de la sesion
   * // Mas informacion sobre Posibles valores en el enum TokenKeysEnum en la carpeta de enums
   */
  public async updateState(tokenKey: TokenKeysEnum, data: DataType): Promise<void> {
    const willUpdate = data !== null;

    if (willUpdate) {
      await this.storage.set(tokenKey, JSON.stringify(data));
    } else {
      await this.storage.remove(tokenKey);
    }
    switch (tokenKey) {
      case TokenKeysEnum.ACCESS_TOKEN:
        this.updateAccessToken = data as string;
        break;
      case TokenKeysEnum.REFRESH_TOKEN:
        this.updateRefreshToken = data as string;
        break;
      case TokenKeysEnum.REFRESH_TOKEN_SERVICE:
        this.updateAccessTokenService = data as string;
        break;
      case TokenKeysEnum.USER:
        this.updateUser = data as IUser;
        break;
      case TokenKeysEnum.EMPLOYEE:
        this.updateEmployee = data as IEmployeeResponse;
        break;
      case TokenKeysEnum.SESSION_EXPIRES:
        this.updateSessionExpires = data as number;
        break;
      default:
        console.error(`
        [LOCAL-DB]
        RAZON: El tokenKey -> ${tokenKey}' <- no existe, revise el enum TokenKeys para ver posibles valores
        y agregue el tokenKey de ser necesario.
        PARAMETROS: ${JSON.stringify(arguments)}
        `);
        break;
    }

  }

  public async clearSession(): Promise<void> {
    await this.updateState(TokenKeysEnum.ACCESS_TOKEN, null);
    await this.updateState(TokenKeysEnum.REFRESH_TOKEN, null);
    await this.updateState(TokenKeysEnum.REFRESH_TOKEN_SERVICE, null);
    await this.updateState(TokenKeysEnum.USER, null);
    await this.updateState(TokenKeysEnum.EMPLOYEE, null);
    await this.updateState(TokenKeysEnum.SESSION_EXPIRES, null);
  }
}
